<template>
    <div class="tool-selector d-flex">
        <div>
            <el-popover
                ref="toolPopover"
                placement="bottom"
                width="620"
                trigger="manual"
                v-model="popoverVisible"
                popper-class="tool-selector-popover"
                :disabled="disabled"
                @hide="onHide"
            >
                <div v-if="!selectedTool">
                    <!-- Search input -->
                    <div class="search-box mb-3">
                        <el-input
                            ref="searchInput"
                            v-model="searchQuery"
                            data-testid="tool-selector-search-input"
                            placeholder="Search actions..."
                            prefix-icon="el-icon-search"
                            clearable
                            size="small"
                            :validate-event="false"
                        />
                    </div>
                    
                    <div class="tool-list">
                        <div
                            v-for="(tool, index) in filteredAndSearchedTools"
                            :key="index"
                            class="tool-item mb-2 p-3"
                            @click="selectTool(tool.id)"
                        >
                            <!-- Tool name and count -->
                            <div class="d-flex align-items-center mb-2">
                                <i :class="[tool.icon, 'tool-icon']" class="mr-2"></i>
                                <strong>{{ tool.name }} <span v-if="toolCounts[tool.id] > 0">({{ toolCounts[tool.id] }})</span></strong>
                            </div>

                            <!-- Tool description -->
                            <div class="tool-description">
                                {{ tool.description }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="tool-content-wrapper">
                    <component
                        v-if="currentToolComponent"
                        :is="currentToolComponent"
                        :tool="selectedToolObj"
                        @back="backToToolList"
                        @property-selected="onPropertySelected"
                        @transfer-call-selected="onTransferCallSelected"
                        @send-sms-selected="onSendSmsSelected"
                        @appointment-selected="onManageAppointmentSelected"
                        @disposition-selected="onDispositionSelected"
                        @call-disposition-selected="onCallDispositionSelected"
                        @tags-selected="onTagsSelected"
                        @communication-tags-selected="onCommunicationTagsSelected"
                        @lists-selected="onListsSelected"
                        @sequence-selected="onSequenceSelected"
                        @sms-sequence-selected="onSmsSequenceSelected"
                        @custom-function-selected="onCustomFunctionSelected"
                        @voicemail-selected="onVoicemailSelected"
                        @hubspot-owner-selected="onHubspotOwnerSelected"
                        @hubspot-ticket-selected="onHubspotTicketSelected"
                    />
                </div>

                <el-button
                    slot="reference"
                    type="primary"
                    plain
                    size="mini"
                    icon="el-icon-plus"
                    class="tool-selector-button my-2"
                    :disabled="disabled"
                    @click="togglePopover"
                >
                    Add Action <span v-if="totalToolCount > 0">({{ totalToolCount }})</span>
                </el-button>
            </el-popover>
        </div>
    </div>
</template>

<script>
import * as AloAi from '../../../constants/aloai'
import AppointmentTool from './tools/appointment-tool'
import BaseTool from './tools/base-tool'
import CallDispositionTool from './tools/call-disposition-tool'
import CommunicationTagsTool from './tools/communication-tags-tool'
import CustomFunctionTool from './tools/custom-function-tool'
import DispositionTool from './tools/disposition-tool'
import HubspotOwnerTool from './tools/hubspot-owner-tool'
import HubspotTicketTool from './tools/hubspot-ticket-tool'
import ListsTool from './tools/lists-tool'
import SendSmsTool from './tools/send-sms-tool'
import SequenceTool from './tools/sequence-tool'
import SmsSequenceTool from './tools/sms-sequence-tool'
import TagsTool from './tools/tags-tool'
import TransferTool from './tools/transfer-tool'
import UpdateContactTool from './tools/update-contact-tool'
import VoicemailTool from './tools/VoicemailTool'

export default {
    name: 'ToolSelector',

    components: {
        BaseTool,
        UpdateContactTool,
        TransferTool,
        SendSmsTool,
        AppointmentTool,
        DispositionTool,
        CallDispositionTool,
        TagsTool,
        CommunicationTagsTool,
        ListsTool,
        SequenceTool,
        SmsSequenceTool,
        CustomFunctionTool,
        VoicemailTool,
        HubspotOwnerTool,
        HubspotTicketTool,
    },

    props: {
        availableTools: {
            type: Array,
            default: () => []
        },
        botType: {
            type: String,
            default: ''
        },
        instructionsContent: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            popoverVisible: false,
            selectedTool: null,
            searchQuery: '',
            AloAi,
            toolCounts: {
                [AloAi.TOOL_UPDATE_CONTACT]: 0,
                [AloAi.TOOL_TRANSFER_CALL]: 0,
                [AloAi.TOOL_SEND_SMS]: 0,
                [AloAi.TOOL_MANAGE_APPOINTMENT]: 0,
                [AloAi.TOOL_ADD_DISPOSITION]: 0,
                [AloAi.TOOL_ADD_CALL_DISPOSITION]: 0,
                [AloAi.TOOL_ADD_TAGS]: 0,
                [AloAi.TOOL_ADD_COMMUNICATION_TAGS]: 0,
                [AloAi.TOOL_ADD_LISTS]: 0,
                [AloAi.TOOL_ENROLL_TO_SEQUENCE]: 0,
                [AloAi.TOOL_DISENGAGE_CONTACT]: 0,
                [AloAi.TOOL_SET_CONTACT_DNC]: 0,
                [AloAi.TOOL_LEAVE_VOICEMAIL]: 0,
                [AloAi.TOOL_CUSTOM_FUNCTION]: 0,
                [AloAi.TOOL_SUMMARIZE_CONVERSATION_AS_NOTE]: 0,
                [AloAi.TOOL_ROUTE_TO_HS_DEAL_CONTACT_OWNER]: 0,
                [AloAi.TOOL_CREATE_HUBSPOT_TICKET]: 0,
                [AloAi.TOOL_ROUTE_TO_EXTENSION]: 0,
                [AloAi.TOOL_EXECUTE_SMS_SEQUENCE]: 0
            },
            tools: [
                {
                    id: AloAi.TOOL_UPDATE_CONTACT,
                    name: 'Update Contact Property',
                    icon: 'el-icon-edit',
                    description: 'Update a property of the contact.'
                },
                {
                    id: AloAi.TOOL_TRANSFER_CALL,
                    name: 'Transfer Call',
                    icon: 'el-icon-phone-outline',
                    description: 'Transfer the current call to another destination.'
                },
                {
                    id: AloAi.TOOL_SEND_SMS,
                    name: 'Send SMS',
                    icon: 'el-icon-message',
                    description: 'Send an SMS to the current contact.'
                },
                {
                    id: AloAi.TOOL_MANAGE_APPOINTMENT,
                    name: 'Manage Appointment',
                    icon: 'el-icon-date',
                    description: 'Create, update, or cancel appointments for the contact when specific condition is met.'
                },
                {
                    id: AloAi.TOOL_ADD_DISPOSITION,
                    name: 'Add Contact Disposition',
                    icon: 'el-icon-tickets',
                    description: 'Add a disposition status to the contact when specific condition is met.'
                },
                {
                    id: AloAi.TOOL_ADD_CALL_DISPOSITION,
                    name: 'Add Call Disposition',
                    icon: 'el-icon-phone',
                    description: 'Add a call disposition to the call when specific condition is met.'
                },
                {
                    id: AloAi.TOOL_ADD_LISTS,
                    name: 'Add Contact to a List',
                    icon: 'el-icon-notebook-2',
                    description: 'Add the contact to a public or private list.'
                },
                {
                    id: AloAi.TOOL_ENROLL_TO_SEQUENCE,
                    name: 'Enroll to Sequence',
                    icon: 'el-icon-magic-stick',
                    description: 'Enroll the contact into an automated sequence/workflow.'
                },
                {
                    id: AloAi.TOOL_ADD_TAGS,
                    name: 'Add Contact Tag',
                    icon: 'el-icon-collection-tag',
                    description: 'Add tag to the contact for categorization.'
                },
                {
                    id: AloAi.TOOL_ADD_COMMUNICATION_TAGS,
                    name: 'Add Communication Tag',
                    icon: 'el-icon-collection-tag',
                    description: 'Add tag to the communication for categorization.'
                },
                {
                    id: AloAi.TOOL_DISENGAGE_CONTACT,
                    name: 'Disengage Contact',
                    icon: 'el-icon-remove-outline',
                    description: 'Prevents this agent from interacting with the contact in the future.'
                },
                {
                    id: AloAi.TOOL_SET_CONTACT_DNC,
                    name: 'Set contact as DNC',
                    icon: 'fa fa-ban',
                    description: 'This will set the contact as DNC (Do Not Call) and prevent them from being contacted again.'
                },
                {
                    id: AloAi.TOOL_LEAVE_VOICEMAIL,
                    name: 'Leave Voicemail',
                    icon: 'fa fa-voicemail',
                    description: 'Route the call to voicemail so the caller can leave a message. This ends the AI conversation.'
                },
                {
                    id: AloAi.TOOL_SUMMARIZE_CONVERSATION_AS_NOTE,
                    name: 'Summarize Conversation as Note',
                    icon: 'fa fa-file-text',
                    description: 'Creates a summary of the current conversation and saves it as a note on the contact.'
                },
                {
                    id: AloAi.TOOL_CUSTOM_FUNCTION,
                    name: 'Custom Function',
                    icon: 'el-icon-setting',
                    description: 'Runs a custom function that is configured in the bot'
                },
                {
                    id: AloAi.TOOL_ROUTE_TO_HS_DEAL_CONTACT_OWNER,
                    name: 'Route to HubSpot Deal Contact Owner',
                    icon: 'el-icon-connection',
                    description: 'Dynamically route calls to the contact\'s assigned CRM owner from HubSpot deals. Requires pipeline selection and a fallback inbox for when no owner is available.'
                },
                {
                    id: AloAi.TOOL_CREATE_HUBSPOT_TICKET,
                    name: 'Create HubSpot Ticket',
                    icon: 'el-icon-document-add',
                    description: 'Create a HubSpot ticket in a specified pipeline and stage. The AloAi agent generates the ticket title and description from the conversation context.'
                },
                {
                    id: AloAi.TOOL_ROUTE_TO_EXTENSION,
                    name: 'Route to Extension',
                    icon: 'fa fa-hashtag',
                    description: 'Voice-based extension directory routing. Allows callers to reach team members by speaking their extension number.'
                },
                {
                    id: AloAi.TOOL_EXECUTE_SMS_SEQUENCE,
                    name: 'SMS Sequence',
                    icon: 'el-icon-s-operation',
                    description: 'Execute a predefined SMS message sequence with the contact.'
                }
            ]
        }
    },

    mounted() {
        // Initialize tool counts when component is mounted
        this.$nextTick(() => {
            this.checkInstructionsToolCounts()
        })

        document.addEventListener('click', this.handleDocumentClick)
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick)
    },

    computed: {
        selectedToolObj() {
            return this.tools.find(tool => tool.id === this.selectedTool) || {}
        },

        filteredTools() {
            if (!this.availableTools || this.availableTools.length === 0) {
                return this.tools
            }

            return this.tools.filter(tool => {
                return this.availableTools.some(availableTool => availableTool.key === tool.id)
            })
        },

        filteredAndSearchedTools() {
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                return this.filteredTools
            }

            const query = this.searchQuery.toLowerCase().trim()
            return this.filteredTools.filter(tool => {
                return tool.name.toLowerCase().includes(query) ||
                       tool.description.toLowerCase().includes(query)
            })
        },

        currentToolComponent() {
            const componentMap = {
                [AloAi.TOOL_UPDATE_CONTACT]: 'UpdateContactTool',
                [AloAi.TOOL_TRANSFER_CALL]: 'TransferTool',
                [AloAi.TOOL_SEND_SMS]: 'SendSmsTool',
                [AloAi.TOOL_MANAGE_APPOINTMENT]: 'AppointmentTool',
                [AloAi.TOOL_ADD_DISPOSITION]: 'DispositionTool',
                [AloAi.TOOL_ADD_CALL_DISPOSITION]: 'CallDispositionTool',
                [AloAi.TOOL_ADD_TAGS]: 'TagsTool',
                [AloAi.TOOL_ADD_COMMUNICATION_TAGS]: 'CommunicationTagsTool',
                [AloAi.TOOL_ADD_LISTS]: 'ListsTool',
                [AloAi.TOOL_ENROLL_TO_SEQUENCE]: 'SequenceTool',
                [AloAi.TOOL_LEAVE_VOICEMAIL]: 'VoicemailTool',
                [AloAi.TOOL_CUSTOM_FUNCTION]: 'CustomFunctionTool',
                [AloAi.TOOL_ROUTE_TO_HS_DEAL_CONTACT_OWNER]: 'HubspotOwnerTool',
                [AloAi.TOOL_CREATE_HUBSPOT_TICKET]: 'HubspotTicketTool',
                [AloAi.TOOL_EXECUTE_SMS_SEQUENCE]: 'SmsSequenceTool',
            }

            return componentMap[this.selectedTool]
        },

        totalToolCount() {
            return Object.values(this.toolCounts).reduce((sum, count) => sum + count, 0)
        }
    },

    methods: {
        selectTool(toolId) {
            if (this.disabled) {
                return
            }

            // Special case for Disengage Contact - immediately adds text to instructions
            if (toolId === AloAi.TOOL_DISENGAGE_CONTACT) {
                this.$emit('disengage-contact-selected', {text: '{Disengage contact}'})

                // Close the popover
                this.hidePopover()
                return
            }

            // Special case for Set contact as DNC - immediately adds text to instructions
            if (toolId === AloAi.TOOL_SET_CONTACT_DNC) {
                this.$emit('set-contact-dnc-selected', {text: '{Set contact as DNC}'})

                // Close the popover
                this.hidePopover()
                return
            }

            // Special case for Summarize Conversation as Note - immediately adds text to instructions
            if (toolId === AloAi.TOOL_SUMMARIZE_CONVERSATION_AS_NOTE) {
                this.$emit('summarize-conversation-selected', {text: '{Summarize conversation as note}'})

                // Close the popover
                this.hidePopover()
                return
            }

            // Special case for Route to Extension - immediately adds text to instructions
            if (toolId === AloAi.TOOL_ROUTE_TO_EXTENSION) {
                this.$emit('route-to-extension-selected', {text: '{Route to extension}'})

                // Close the popover
                this.hidePopover()
                return
            }

            // Regular case for other tools, open the popover
            this.selectedTool = toolId
        },

        /**
         * Back to the tool card list
         */
        backToToolList() {
            this.selectedTool = null
        },

        onDispositionSelected(disposition) {
            this.$emit('disposition-selected', disposition)
            if (disposition) {
                this.hidePopover()
            }
        },

        onCallDispositionSelected(disposition) {
            this.$emit('call-disposition-selected', disposition)
            if (disposition) {
                this.hidePopover()
            }
        },

        onTagsSelected(tags) {
            this.$emit('tags-selected', tags)
            if (tags && tags.length) {
                this.hidePopover()
            }
        },

        onCommunicationTagsSelected(tags) {
            this.$emit('communication-tags-selected', tags)
            if (tags && tags.length) {
                this.hidePopover()
            }
        },

        onListsSelected(listIds, listItems) {
            this.$emit('lists-selected', listIds, listItems)
            if (listIds && listIds.length) {
                this.hidePopover()
            }
        },

        onSequenceSelected(sequenceId, sequenceItem) {
            this.$emit('sequence-selected', sequenceId, sequenceItem)
            if (sequenceId) {
                this.hidePopover()
            }
        },

        onSmsSequenceSelected(sequenceId, sequenceItem) {
            this.$emit('sms-sequence-selected', sequenceId, sequenceItem)
            if (sequenceId) {
                this.hidePopover()
            }
        },

        onPropertySelected(property) {
            this.$emit('property-selected', property)
            if (property) {
                this.hidePopover()
            }
        },

        onTransferCallSelected(transferData) {
            this.$emit('transfer-call-selected', transferData)
            if (transferData) {
                this.hidePopover()
            }
        },

        onManageAppointmentSelected(appointment) {
            this.$emit('manage-appointment-selected', appointment)
            if (appointment) {
                this.popoverVisible = false
            }
        },

        onSendSmsSelected(message) {
            this.$emit('send-sms-selected', message)
            if (message) {
                this.hidePopover()
            }
        },

        onCustomFunctionSelected(customFunction) {
            this.$emit('custom-function-selected', {
                text: '{Run this function: '+customFunction.name+'|' + customFunction.key + '}'
            })
            this.hidePopover()
        },

        onVoicemailSelected(voicemail) {
            this.$emit('voicemail-selected', voicemail)
            if (voicemail) {
                this.hidePopover()
            }
        },

        onHubspotOwnerSelected(hubspotOwnerData) {
            this.$emit('hubspot-owner-selected', hubspotOwnerData)
            if (hubspotOwnerData) {
                this.hidePopover()
            }
        },

        onHubspotTicketSelected(hubspotTicketData) {
            this.$emit('hubspot-ticket-selected', hubspotTicketData)
            if (hubspotTicketData) {
                this.hidePopover()
            }
        },

        /**
         * Hide the popover
         */
        hidePopover() {
            this.popoverVisible = false
            // Reset search when closing
            this.searchQuery = ''
        },

        /**
         * Check if the instructions contain any tool markers
         * for card counts of each selected tool
         */
        checkInstructionsToolCounts() {
            // If availableTools is null, we can't properly manipulate the selection
            if (!this.availableTools) {
                return
            }

            // Reset all counts to 0 first
            Object.keys(this.toolCounts).forEach(key => {
                this.toolCounts[key] = 0
            })

            // If instructionsContent is empty, no need to count
            if (!this.instructionsContent) {
                return
            }

            // Define individual regex patterns for each tool type
            // These patterns must match exactly with backend TOOL_DETECTION_PATTERNS
            const toolPatterns = {
                [AloAi.TOOL_UPDATE_CONTACT]: [
                    /\{Collect and update:.*?\}/gi
                ],
                [AloAi.TOOL_TRANSFER_CALL]: [
                    /\{Transfer to this line:.*?\}/gi,
                    /\{Transfer to this ring group:.*?\}/gi,
                    /\{Transfer to this user:.*?\}/gi
                ],
                [AloAi.TOOL_SEND_SMS]: [
                    /\{Send SMS:.*?\}/gi
                ],
                [AloAi.TOOL_MANAGE_APPOINTMENT]: [
                    /\{Manage appointment with:.*?\}/gi
                ],
                [AloAi.TOOL_ADD_DISPOSITION]: [
                    /\{Add .*? disposition to the contact\}/gi
                ],
                [AloAi.TOOL_ADD_CALL_DISPOSITION]: [
                    /\{Add .*? call disposition to the call\}/gi
                ],
                [AloAi.TOOL_ADD_TAGS]: [
                    /\{Add .*? tag\(s\) to the contact\}/gi
                ],
                [AloAi.TOOL_ADD_COMMUNICATION_TAGS]: [
                    /\{Add .*? tag\(s\) to the communication\}/gi
                ],
                [AloAi.TOOL_ADD_LISTS]: [
                    /\{Add contact to .*? list\(s\)\}/gi
                ],
                [AloAi.TOOL_ENROLL_TO_SEQUENCE]: [
                    /\{Enroll to sequence:.*?\}/gi
                ],
                [AloAi.TOOL_DISENGAGE_CONTACT]: [
                    /\{Disengage contact\}/gi
                ],
                [AloAi.TOOL_SET_CONTACT_DNC]: [
                    /\{Set contact as DNC\}/gi
                ],
                [AloAi.TOOL_LEAVE_VOICEMAIL]: [
                    /\{Leave voicemail:.*?\}/gi
                ],
                [AloAi.TOOL_CUSTOM_FUNCTION]: [
                    /\{Run this function:.*?\}/gi
                ],
                [AloAi.TOOL_SUMMARIZE_CONVERSATION_AS_NOTE]: [
                    /\{Summarize conversation as note\}/gi
                ],
                [AloAi.TOOL_ROUTE_TO_HS_DEAL_CONTACT_OWNER]: [
                    /\{Route to HubSpot deal contact owner:.*?\}/gi
                ],
                [AloAi.TOOL_CREATE_HUBSPOT_TICKET]: [
                    /\{Create HubSpot ticket:.*?\}/gi
                ],
                [AloAi.TOOL_ROUTE_TO_EXTENSION]: [
                    /\{Route to extension\}/gi
                ],
                [AloAi.TOOL_EXECUTE_SMS_SEQUENCE]: [
                    /\{Execute SMS Sequence:.*?\}/gi
                ]
            }

            // Count each tool type by checking all its patterns
            Object.keys(this.toolCounts).forEach(toolKey => {
                let totalCount = 0
                const patterns = toolPatterns[toolKey] || []
                
                patterns.forEach(pattern => {
                    const matches = this.instructionsContent.match(pattern) || []
                    totalCount += matches.length
                })
                
                this.toolCounts[toolKey] = totalCount
            })
        },

        openToolSelector() {
            if (!this.disabled) {
                this.popoverVisible = true
            }
        },

        togglePopover() {
            if (this.disabled) {
                return
            }

            this.popoverVisible = !this.popoverVisible
        },

        handleDocumentClick(event) {
            if (!this.popoverVisible) {
                return
            }

            const popoverEl = this.$refs.toolPopover?.$refs?.popper
            const referenceEl = this.$el

            if (!popoverEl || !referenceEl) {
                return
            }

            const clickedInside = popoverEl.contains(event.target) || referenceEl.contains(event.target)

            // If a tool form is open, don't close on outside click
            if (!clickedInside && !this.selectedTool) {
                this.popoverVisible = false
            }
        },

        onHide() {
            this.$emit('hide')
        }
    },

    watch: {
        popoverVisible(newValue) {
            if (!newValue) {
                setTimeout(() => {
                    this.selectedTool = null
                    this.searchQuery = ''
                }, 1000)
            } else {
                // Focus the search input when popover opens
                this.$nextTick(() => {
                    if (this.$refs.searchInput && !this.selectedTool) {
                        this.$refs.searchInput.focus()
                    }
                })
            }
        },

        instructionsContent: {
            handler() {
                this.checkInstructionsToolCounts()
            },
            immediate: true
        },

        availableTools: {
            handler(newValue) {
                if (newValue && newValue.length > 0) {
                    this.$nextTick(() => {
                        this.checkInstructionsToolCounts()
                    })
                }
            },
            immediate: true
        }
    }
}
</script>

<style scoped>
.tool-selector {
    display: flex;
    width: 100%;
}

.tool-list {
    max-height: 350px;
    overflow-y: auto;
}

.tool-item {
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #EBEEF5;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.tool-item:hover {
    background-color: #f5f7fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.tool-description {
    font-size: 0.85rem;
    color: #606266;
    line-height: 1.4;
}

.tool-content-wrapper {
    max-height: 450px;
    overflow-y: auto;
}

.tool-icon {
    font-size: 18px;
    color: #409EFF;
}

.tool-selector-button {
    margin-top: 8px;
    margin-bottom: 8px;
}

.search-box {
    padding: 0;
}

.search-box >>> .el-input {
    width: 100%;
}

.search-box >>> .el-input__validateIcon {
    display: none !important;
}

.search-box >>> .el-input__icon.el-icon-circle-check {
    display: none !important;
}
</style>
